import { useRef, useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import {
  ConfirmButton,
  CopyButton,
  FileLink,
  ModalButtonWithForm
} from 'components/common';
import { FileUploader } from 'components/common/uploader';

import type { CertificateTemplate } from 'shared-types';

interface Props {
  onUpdate: () => Promise<void>;
  template: CertificateTemplate;
}

const CertificatesFiles = ({ onUpdate, template }: Props) => {
  const uploader = useRef<any>(undefined);

  const { dispatch } = useDispatch();
  const { messages, theme } = useReduxState();

  const [fileAdded, setFileAdded] = useState(false);

  const handleAddFile = async () => {
    if (!fileAdded) return;

    try {
      const result = await uploader.current.upload();
      if (!result) return false;

      const response = await api.put(
        `/admin/certificate-templates/${template.id}`,
        {
          files: [
            ...(template.files || []),
            { name: result.filename, url: result.url }
          ]
        }
      );
      if (!response.data?.success) return false;

      await onUpdate();

      dispatch.addNotice({
        text: messages.siteSettingsFilesUploadSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setFileAdded(false);
    }
  };

  const handleDeleteFile = async (fileIndex) => {
    const data = {
      files: [
        ...template.files.slice(0, fileIndex),
        ...template.files.slice(fileIndex + 1)
      ]
    };

    const response = await api.put(
      `/admin/certificate-templates/${template.id}`,
      data
    );

    if (!response.data?.success) return false;

    await onUpdate();

    return true;
  };

  const handleFileAdded = () => {
    setFileAdded(true);
  };

  const handleFileRemoved = () => {
    setFileAdded(false);
  };

  const handleModalHide = () => {
    if (fileAdded && uploader.current?.state.file?.id) {
      uploader.current.handleRemove();
    }
  };

  return (
    <div className="files">
      <div className="list">
        {template?.files?.map((file, fileIndex) => (
          <div className="file" key={file.url}>
            <div className="file-main">
              <div className="file-link">
                <FileLink name={file.name} url={file.url} />
              </div>

              <div className="file-copy">
                <CopyButton
                  category="light"
                  icon={['far', 'copy']}
                  size="tiny"
                  successText={messages.commonCopied}
                  tooltip={messages.commonCopyLink}
                  value={file.url}
                />
              </div>
            </div>

            <div className="file-delete">
              <ConfirmButton
                actionCategory="danger"
                actionText={messages.commonDelete}
                buttonIcon="trash-alt"
                buttonSize="small"
                buttonTooltip={messages.commonDelete}
                category="light"
                confirmText={messages.siteSettingsFilesDeleteConfirm}
                confirmTitle={messages.siteSettingsFilesDelete}
                onConfirm={() => handleDeleteFile(fileIndex)}
                successMessage={messages.siteSettingsFilesDeleteSuccess}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="add">
        <ModalButtonWithForm
          buttonCategory="light"
          buttonSize="small"
          buttonText={messages.commonUploadFile}
          modalActionCategory="success"
          modalActionText={messages.commonUploadFile}
          modalTitle={messages.commonUploadFile}
          onHide={handleModalHide}
          onSubmit={handleAddFile}
          preventSubmit={true}
          submitDisabled={!fileAdded}
        >
          {() => (
            <div className="content">
              <FileUploader
                dragDrop={true}
                dragDropActions={false}
                getUploader={(fileUploader) =>
                  (uploader.current = fileUploader)
                }
                icon="file"
                id="site-settings-files-upload-file"
                keyPrefix="uploads/files/"
                onFileAdded={handleFileAdded}
                onFileRemoved={handleFileRemoved}
                uuidAsFilename={false}
              />
            </div>
          )}
        </ModalButtonWithForm>
      </div>

      <style jsx>{`
        .list {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.inputBorderRadius};
        }
        .file {
          align-items: center;
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.5rem;
          font-weight: 600;
          gap: 10px;
          padding: 5px 10px;
        }
        .file-main {
          align-items: center;
          display: flex;
          flex-grow: 1;
          gap: 10px;
        }
        .file:not(:first-child) {
          border-top: 1px solid ${theme.lightGreyDark};
        }
        .add {
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default CertificatesFiles;
